import classNames from "classnames";
import React, { FunctionComponent, memo, RefObject, useMemo } from "react";
import { useIsDesktop } from "../../hooks";
import { ButtonProps } from "../button";
import { LinkProps } from "../link";
import { LightDarkVariant, LogoRenderer, NavMenuItem } from "../types";
import { CSS_CLASSES } from "./global-header-css-classes";
import { GlobalHeaderMainBar } from "./global-header-main-bar";
import { GlobalHeaderUserMenuProps } from "./global-header-user-menu";
import { GlobalHeaderUtilityBarRenderer } from "./global-header-utility-bar-renderer";

export type GlobalHeaderUtilityHeaderLinksAlignment =
  | "left"
  | "center"
  | "right"
  | "space-between"
  | "space-around"
  | "space-evenly";

export interface GlobalHeaderButtonProps extends ButtonProps {
  /**
   * This property controls where a particular CTA button is rendered on mobile.
   * Since space on the main header is limited on mobile, we can use this property to mark which of the CTA buttons
   * continues to be displayed on the main header. The other are placed within the mobile menu.
   */
  placementOnMobile: "main-header" | "mobile-menu";

  /**
   * This property controls if a particular CTA button is hidden when the user is indicated to be signed in.
   */
  hideWhenSignedIn?: boolean;
}

export interface GlobalHeaderUtilityHeaderLinkProps extends Omit<LinkProps, "startIcon" | "endIcon"> {
  /**
   * This property controls if a particular link is hidden when the user is indicated to be signed in.
   */
  hideWhenSignedIn?: boolean;
}

export interface GlobalHeaderUtilityHeaderIconLinkProps extends GlobalHeaderUtilityHeaderLinkProps {
  startIcon: LinkProps["startIcon"];

  /**
   * This property controls where a particular icon-link is rendered on mobile:
   * - either continues to be displayed on the "utility header"
   *   OR
   * - drops down in to the "mobile menu"
   *
   * Since space on the mobile utility header is limited, only upto two icon-links can be displayed there.
   */
  placementOnMobile: "utility-header" | "mobile-menu";
}

export interface GlobalHeaderProps {
  /**
   * This prop controls the fundamental style variant of the Global Header.
   *
   * @default light
   */
  variant?: LightDarkVariant;

  /**
   * Color option for the navigation and user menus.
   *
   * @default interactive
   */
  menuColor?: "interactive" | "primary";

  /**
   * A render-prop function for the header logo.
   * It is called with the current header color `variant` and `isDesktop` as props.
   */
  logoRenderer?: LogoRenderer;

  /**
   * Items for the main navigation menu.
   */
  navMenuItems: NavMenuItem[];

  /**
   * This prop controls whether the main nav menu on Desktop reveals its sub-menu (if present) on `hover`.
   *
   * If set to `false` the sub-menu is revealed on `click`.
   *
   * @default true
   */
  openDesktopNavSubMenuOnHover?: boolean;

  /**
   * Event triggered when a particular nav menu item is activated by user interaction.
   * Handler is called with the id of the activated menu item, `null` if all menu items
   * were caused to be de-activated.
   */
  onNavMenuItemActivate: (id: string | null) => void;

  /**
   * List of Button props objects to be used to render the set of CTA buttons on the global header.
   */
  ctaButtons?: GlobalHeaderButtonProps[];

  /**
   * Controls if the header is rendered in a state assuming active logged in user or not.
   *
   * @default false
   */
  isUserSignedIn?: boolean;

  /**
   * Props to pass along to the GlobalHeaderUserMenu component
   */
  userMenuProps?: Omit<GlobalHeaderUserMenuProps, "isDesktop">;

  /**
   * List of `Link` component props objects to be used to render the set of links on the utility bar of the global header.
   */
  utilityHeaderLinks?: GlobalHeaderUtilityHeaderLinkProps[];

  /**
   * List of `Link` component props objects to be used to render the set of icon links on the utility bar of the global header.
   *
   * The props object accepts all `Link` component props except `endIcon`.
   * The props object additionally accepts the `placementOnMobile` property. Set it to `utility-header` to display the
   * icon-link on the mobile utility bar. There is room for only two icon links on the mobile utility bar.
   * Others should be set to `placementOnMobile: "mobile-menu"`.
   */
  utilityHeaderIconLinks?: GlobalHeaderUtilityHeaderIconLinkProps[];

  /**
   * This prop controls the way the links clusters in the utility bar are aligned.
   *
   * @default right
   */
  utilityHeaderLinksAlignment?: GlobalHeaderUtilityHeaderLinksAlignment;

  /**
   * Controls if the header is rendered as a sticky header, with respect to page scrolling.
   *
   * @default false
   */
  sticky?: boolean;

  /**
   * Control if the header content is constrained to a max-width based on viewport size.
   *
   * @default false
   */
  constrainContentWidth?: boolean;

  /**
   * Use this to pass a ref that will be assigned to the ref prop of the root HTML element in this components render
   * tree.
   */
  innerRef?: RefObject<HTMLElement>;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * Controls the responsive view rendered by this component. By default it renders the `mobile` view.
   *
   * It also uses the `ScreenSizeContextProvider` is present, and will use the `isDesktop` value from it. However, if
   * this prop is passed the it is given precedence over the value from the context provider.
   *
   * It is recommended to use `ScreenSizeContextProvider` and place the `GlobalHeader` component within it for
   * the responsive view to work automatically.
   */
  desktop?: boolean;
}

/**
 * The `GlobalHeader` component represents a header element that's to be displayed across all pages of a website or screens of a web-app. It is composed of several elements, like brand logo, navigation menu and CTA buttons.
 */
export const GlobalHeader: FunctionComponent<GlobalHeaderProps> = memo(
  ({
    variant = "light",
    menuColor = "interactive",
    logoRenderer,
    navMenuItems = [],
    openDesktopNavSubMenuOnHover = true,
    onNavMenuItemActivate,
    ctaButtons = [],
    isUserSignedIn = false,
    userMenuProps,
    utilityHeaderLinks = [],
    utilityHeaderIconLinks = [],
    utilityHeaderLinksAlignment = "right",
    sticky = false,
    constrainContentWidth = false,
    innerRef,
    className,
    desktop: isDesktopProp,
  }) => {
    const isDesktopContextValue = useIsDesktop(true);
    const isDesktop =
      isDesktopProp !== undefined ? isDesktopProp : isDesktopContextValue !== undefined ? isDesktopContextValue : false;

    const rootClassName = useMemo(
      () =>
        classNames(
          CSS_CLASSES.GLOBAL_HEADER_ROOT,
          {
            [CSS_CLASSES.GLOBAL_HEADER_STICKY]: sticky,
            [CSS_CLASSES.GLOBAL_HEADER_DESKTOP]: isDesktop,
            [CSS_CLASSES.GLOBAL_HEADER_MOBILE]: !isDesktop,
          },
          className
        ),
      [className, isDesktop, sticky]
    );

    const userMenuPropsToPass = useMemo(
      () =>
        userMenuProps && utilityHeaderIconLinks.length > 0
          ? { ...userMenuProps, addTopOffsetToMobileMenu: true }
          : userMenuProps,
      [userMenuProps, utilityHeaderIconLinks]
    );

    return (
      <header ref={innerRef} className={rootClassName}>
        <GlobalHeaderUtilityBarRenderer
          variant={variant}
          links={utilityHeaderLinks}
          iconLinks={utilityHeaderIconLinks}
          linksAlignment={isDesktop ? utilityHeaderLinksAlignment : "center"}
          isUserSignedIn={isUserSignedIn}
          constrainContentWidth={isDesktop ? constrainContentWidth : false}
          isDesktop={isDesktop}
        />
        <GlobalHeaderMainBar
          variant={variant}
          menuColor={menuColor}
          logoRenderer={logoRenderer}
          navMenuItems={navMenuItems}
          openDesktopNavSubMenuOnHover={openDesktopNavSubMenuOnHover}
          onNavMenuItemActivate={onNavMenuItemActivate}
          utilityHeaderLinks={utilityHeaderLinks}
          utilityHeaderIconLinks={utilityHeaderIconLinks}
          ctaButtons={ctaButtons}
          isUserSignedIn={isUserSignedIn}
          userMenuProps={userMenuPropsToPass}
          constrainContentWidth={isDesktop ? constrainContentWidth : false}
          isDesktop={isDesktop}
        />
      </header>
    );
  }
);

GlobalHeader.displayName = "GlobalHeader";
